import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import CTAButton from "../CTAButton";

interface RegistrationPricingSectionProps {
  totalValue?: string;
  regularPrice?: string;
  todayPrice?: string;
  buttonText?: string;
  buttonUrl?: string;
  timerText?: string;
  bonusValue?: string;
  eventDate?: string;
  eventTime?: string;
  eventLanguage?: string;
}

const RegistrationPricingSection: React.FC<RegistrationPricingSectionProps> = ({
  totalValue = "₹7,486",
  regularPrice = "₹999",
  todayPrice = "₹299/- Only",
  buttonText = "REGISTER NOW AT ₹299/- ONLY",
  buttonUrl = "https://calendly.com/zylusales/group-growth-consultation?hide_gdpr_banner=1",
  timerText = "Reserve Your Seat Before The Timer Ends To Unlock To End Bonuses Worth 6,487/-",
  bonusValue = "6,487",
  eventDate = "October 30th",
  eventTime = "1:00 PM IST",
  eventLanguage = "Basic English"
}) => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      // Set target date: October 30th, 2025 at 1:00 PM IST
      const targetDate = new Date('2025-10-30T13:00:00+05:30'); // IST timezone
      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();

      if (difference > 0) {
        const hours = Math.floor(difference / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        return { hours, minutes };
      } else {
        // Event has passed, show 0
        return { hours: 0, minutes: 0 };
      }
    };

    // Set initial time
    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">
            Registration & Pricing
          </h2>
          <div className="w-24 h-1 bg-red-500 mx-auto"></div>
        </motion.div>

        {/* Combined Card - Event Details, Pricing, and Timer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl p-8 shadow-xl"
        >
          {/* Event Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center mb-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Date</h3>
              <p className="text-xl font-bold text-black">{eventDate}</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Time</h3>
              <p className="text-xl font-bold text-black">{eventTime}</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Language</h3>
              <p className="text-xl font-bold text-black">{eventLanguage}</p>
            </div>
          </div>

          {/* Pricing Section */}
          <div className="text-center mb-8">
            {/* Today's Price */}
            <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-8 mb-8 border-4 border-red-200">
              <h3 className="text-2xl font-bold text-gray-700 mb-4">Today's Price</h3>
              <div className="text-5xl font-bold text-red-600 mb-6">
                ₹299/- Only
              </div>
              
              {/* CTA Button */}
              <CTAButton
                text={buttonText}
                calendlyUrl={buttonUrl}
                className="text-xl py-4 px-8 uppercase tracking-wide font-bold bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-2xl rounded-xl"
                icon={false}
                isWebinarRegistration={true}
              />
            </div>
          </div>

          {/* Countdown Timer */}
          <div className="flex justify-center space-x-4">
            {/* Hours */}
            <div className="bg-gray-100 rounded-xl p-6 text-center">
              <div className="text-4xl font-bold text-black mb-2">
                {timeLeft.hours.toString().padStart(2, '0')}
              </div>
              <div className="text-sm text-black uppercase tracking-wide">
                Hours
              </div>
            </div>

            {/* Minutes */}
            <div className="bg-gray-100 rounded-xl p-6 text-center">
              <div className="text-4xl font-bold text-black mb-2">
                {timeLeft.minutes.toString().padStart(2, '0')}
              </div>
              <div className="text-sm text-black uppercase tracking-wide">
                Minutes
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default RegistrationPricingSection;
